import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Heading,
  Divider,
  VStack,
  Flex,
  Button,
  useToast,
  ButtonGroup,
  Grid,
  GridItem,
} from '@chakra-ui/react';

import axios from 'axios';
import { AvatarInput } from '../../../../../components/Form/AvatarInput';
import { MaskedInput } from '../../../../../components/Form/MaskedInput';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { InternationalPhoneInput } from '../../../../../components/Form/InternationalPhoneInput';
import {
  maskCpf,
  maskZipCode,
} from '../../../../../utils/formatters/handleMask';
import {
  IDetailedUser,
  showUsersService,
} from '../../../../../services/Users/ShowUsersService';
import deleteUserAvatarsService from '../../../../../services/Users/DeleteUserAvatarsService';
import { updateUserAvatarsService } from '../../../../../services/Users/UpdateUserAvatarsService';
import { translateError } from '../../../../../utils/errors';
import { updateUsersService } from '../../../../../services/Users/UpdateUsersService';
import {
  validateCpf,
  validateBRPhone,
} from '../../../../../utils/documentValidation';
import { DatePicker } from '../../../../../components/Form/DatePicker';
import { UserAvatarStatus } from '../../../../../models/users';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { IAddressBase } from '../../../../../models/address';
import AddressForm, {
  addressValidation,
} from '../../../../../components/AddressForm';
import FaceIdStatusTag from '../../components/FaceIDStatusTag';
import AvatarStatusCard from '../../components/AvatarStatusCard';
import { checkUsersFaceService } from '../../../../../services/Users/CheckUsersFaceService';
import { useAuth } from '../../../../../hooks/auth';
import {
  ReactSelect,
  SelectOption,
} from '../../../../../components/Form/ReactSelect';
import { listUserCategoriesService } from '../../../../../services/UserCategories/ListUserCategoriesService';

type UpdateMemberFormData = {
  address: IAddressBase;
  bio?: string;
  birthDate: Date;
  categoryId: string;
  cpf?: string;
  email: string;
  healthCheck?: Date;
  name: string;
  phone?: string;
};

interface ILocationState {
  memberId: string;
}

const memberUpdateFormSchema = Yup.object().shape({
  address: addressValidation.nullable().transform((value, originalValue) => {
    if (
      !!originalValue &&
      Object.values<string | undefined>(originalValue).some(
        (val) => !!val?.length,
      )
    ) {
      return originalValue;
    }

    return null;
  }),
  bio: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  birthDate: Yup.date().nullable().required('Data de nascimento requerida'),
  categoryId: Yup.string().nullable().required('Categoria requerida'),
  cpf: Yup.string()
    .length(11, 'Valor inválido, requerido 11 dígitos')
    .test('is-valid', 'CPF inválido', (value) => validateCpf(value))
    .nullable()
    .transform((_, originalValue) =>
      originalValue.replace(/\D/g, '').length
        ? originalValue.replace(/\D/g, '')
        : null,
    ),
  email: Yup.string()
    .email('E-mail inválido')
    .required('E-mail requerido')
    .transform((value) => value.toLowerCase()),
  healthCheck: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  name: Yup.string()
    .required('Nome requerido')
    .matches(/^[a-zA-ZÀ-ÿ\s]+$/, 'Nome inválido'),
  phone: Yup.string()
    .test('is-valid', 'Telefone inválido', (value) => validateBRPhone(value))
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

export const MemberUpdate = (): JSX.Element => {
  const { goBack } = useHistory();
  const toast = useToast();
  const {
    updateUser,
    user: authenticatedUser,
    venture: authenticatedVenture,
  } = useAuth();

  const { state } = useLocation<ILocationState>();

  const [updatingMember, setUpdatingMember] = useState<IDetailedUser>();
  const [avatar, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string>();
  const [isCheckingAvatar, setIsCheckingAvatar] = useState(false);
  const [avatarStatus, setAvatarStatus] = useState<UserAvatarStatus>(
    UserAvatarStatus.EMPTY,
  );
  const [categorySelectOptions, setcategorySelectOptions] = useState<
    SelectOption[]
  >([]);
  const [
    isDeleteConfirmationModalVisible,
    setIsDeleteConfirmationModalVisible,
  ] = useState(false);

  const formMethods = useForm({
    resolver: yupResolver(memberUpdateFormSchema),
  });

  const { register, handleSubmit, formState, reset, control } = formMethods;

  const { errors } = formState;
  const { memberId } = state;

  useEffect(() => {
    async function loadCategories(ventureId: string): Promise<void> {
      try {
        const userCategories = await listUserCategoriesService(ventureId);

        setcategorySelectOptions(
          userCategories.map((userCategory) => ({
            label: userCategory.title,
            value: userCategory.id,
          })),
        );
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar as categorias disponíveis, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }

    if (authenticatedVenture) {
      loadCategories(authenticatedVenture.id);
    }
  }, [authenticatedVenture, toast]);

  useEffect(() => {
    async function loadMember(): Promise<void> {
      try {
        const memberData = await showUsersService(memberId);

        setAvatarUrl(memberData.avatarUrl || undefined);

        setAvatarStatus(memberData.avatarStatus);

        setUpdatingMember(memberData);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados do membro, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }

    loadMember();
  }, [memberId, reset, toast]);

  useEffect(() => {
    if (!!updatingMember && !!authenticatedVenture) {
      const categoryId = updatingMember.categories.find(
        (category) => category.ventureId === authenticatedVenture.id,
      )?.id;

      reset({
        address: {
          city: updatingMember.address?.city,
          complement: updatingMember.address?.complement,
          country: updatingMember.address?.country,
          neighborhood: updatingMember.address?.neighborhood,
          number: updatingMember.address?.number,
          zipCode: updatingMember.address?.zipCode
            ? maskZipCode(updatingMember.address?.zipCode)
            : undefined,
          state: updatingMember.address?.state,
          street: updatingMember.address?.street,
        },
        bio: updatingMember.userAssignments[0]?.bio,
        birthDate: updatingMember.birthDate
          ? new Date(updatingMember.birthDate)
          : undefined,
        categoryId,
        cpf: updatingMember.cpf ? maskCpf(updatingMember.cpf) : undefined,
        email: updatingMember.email,
        healthCheck: updatingMember.healthCheck
          ? new Date(updatingMember.healthCheck)
          : undefined,
        name: updatingMember.name,
        phone: updatingMember.phone,
      });
    }
  }, [authenticatedVenture, reset, updatingMember]);

  const checkUserAvatar = useCallback(
    async (file: File) => {
      setIsCheckingAvatar(true);

      try {
        const formData = new FormData();
        formData.append('face', file);

        const { avatarStatus: verifiedAvatarStatus } =
          await checkUsersFaceService({
            faceData: formData,
          });

        setAvatarStatus(verifiedAvatarStatus);

        if (verifiedAvatarStatus === UserAvatarStatus.VALID) {
          toast({
            title: 'Foto validada',
            description: 'A foto foi validada com sucesso',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      } catch (err) {
        setAvatarStatus(UserAvatarStatus.NOT_VERIFIED);

        toast({
          title: 'Foto não validada',
          description: 'Ocorreu um erro ao validar a foto',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } finally {
        setIsCheckingAvatar(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    if (
      !!avatar &&
      !!updatingMember?.userAssignments[0]?.featureGroup?.useFaceId
    ) {
      checkUserAvatar(avatar);
    }
  }, [avatar, checkUserAvatar, updatingMember?.userAssignments]);

  const handleChangeAvatar = useCallback((file: File) => {
    setAvatar(file);
    setAvatarUrl(URL.createObjectURL(file));
  }, []);

  const handleToggleDeleteConfirmationModal = useCallback(() => {
    setIsDeleteConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleDeleteAvatar = useCallback(async () => {
    await deleteUserAvatarsService(memberId);

    setAvatar(undefined);

    setAvatarUrl(undefined);

    setAvatarStatus(UserAvatarStatus.EMPTY);

    handleToggleDeleteConfirmationModal();
  }, [memberId, handleToggleDeleteConfirmationModal]);

  const handleUpdateMember: SubmitHandler<UpdateMemberFormData> = useCallback(
    async ({
      address,
      bio,
      birthDate,
      categoryId,
      cpf,
      email,
      healthCheck,
      name,
      phone,
    }) => {
      if (!!updatingMember && !!authenticatedVenture) {
        try {
          const updatedMember = await updateUsersService({
            userId: updatingMember.id,
            address,
            bio,
            birthDate,
            categoriesId: [categoryId],
            cpf,
            email,
            featureGroupId: updatingMember.userAssignments[0]?.featureGroupId,
            healthCheck,
            name,
            phone,
          });

          if (avatar) {
            const formData = new FormData();

            formData.append('avatar', avatar);

            const memberWithUpdatedAvatar = await updateUserAvatarsService({
              userId: updatingMember.id,
              avatarData: formData,
            });

            if (memberWithUpdatedAvatar.id === authenticatedUser.id) {
              updateUser({
                ...memberWithUpdatedAvatar,
                userAssignments: authenticatedUser.userAssignments,
              });
            }
          } else if (updatedMember.id === authenticatedUser.id) {
            updateUser({
              ...updatedMember,
              userAssignments: authenticatedUser.userAssignments,
            });
          }

          toast({
            title: 'Editado com sucesso',
            description: 'O membro foi editado corretamente.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });

          goBack();
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status !== 401) {
            toast({
              title: 'Falha ao editar',
              description:
                translateError({ message: err.response?.data.message }) ||
                'Ocorreu um erro ao editar o membro, tente novamente.',
              status: 'error',
              duration: 3000,
              isClosable: true,
              variant: 'subtle',
              position: 'top-right',
            });
          }
        }
      }
    },
    [
      updatingMember,
      authenticatedVenture,
      avatar,
      authenticatedUser.id,
      authenticatedUser.userAssignments,
      toast,
      goBack,
      updateUser,
    ],
  );

  return (
    <DefaultLayout>
      <ConfirmationModal
        isOpen={isDeleteConfirmationModalVisible}
        onClose={handleToggleDeleteConfirmationModal}
        onConfirm={handleDeleteAvatar}
        title="Confirmar exclusão"
        message="Deseja realmente excluir?"
      />

      <FormProvider {...formMethods}>
        <Box
          as="form"
          flex="1"
          borderRadius={8}
          bg="white"
          p="8"
          onSubmit={handleSubmit(handleUpdateMember)}
        >
          <Heading size="lg" fontWeight="normal">
            Editar membro
          </Heading>

          <Divider my="6" borderColor="gray.300" />

          <Flex direction="column" align="center" mb="8">
            <Box mb={6} alignSelf="flex-end">
              <FaceIdStatusTag
                avatarChanged={
                  !!updatingMember?.userAssignments[0]?.featureGroup
                    .useFaceId && !!avatar
                }
                avatarStatus={avatarStatus}
                faceIdStatus={updatingMember?.faceIdStatus}
              />
            </Box>

            <AvatarInput
              avatarUrl={avatarUrl}
              onChange={handleChangeAvatar}
              onDelete={handleToggleDeleteConfirmationModal}
              showWebcamButton
            />

            <Box mt={6}>
              <AvatarStatusCard avatarStatus={avatarStatus} />
            </Box>

            {!!avatar &&
              (avatarStatus === UserAvatarStatus.NOT_VERIFIED ||
                isCheckingAvatar) && (
                <Box mt={3}>
                  <Button
                    isLoading={isCheckingAvatar}
                    loadingText="Validando foto..."
                    colorScheme="blue"
                    onClick={() => checkUserAvatar(avatar)}
                  >
                    Validar foto
                  </Button>
                </Box>
              )}
          </Flex>

          <VStack spacing="8">
            <MaskedInput
              label="Nome completo"
              error={errors.name}
              {...register('name')}
            />

            <Grid
              templateColumns={[
                'repeat(1, 1fr)',
                'repeat(1, 1fr)',
                'repeat(12, 1fr)',
                'repeat(12, 1fr)',
                'repeat(12, 1fr)',
              ]}
              columnGap="4"
              rowGap="8"
              width="100%"
            >
              <GridItem colSpan={[12, 12, 6]}>
                <MaskedInput
                  label="CPF"
                  mask="cpf"
                  error={errors.cpf}
                  {...register('cpf')}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <DatePicker
                  label="Data de nascimento"
                  isClearable={false}
                  maxDate={new Date()}
                  control={control}
                  error={errors.birthDate}
                  {...register('birthDate')}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <InternationalPhoneInput
                  label="Telefone"
                  name="phone"
                  control={control}
                  error={errors.phone}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <MaskedInput
                  label="E-mail"
                  type="email"
                  textTransform="lowercase"
                  error={errors.email}
                  {...register('email')}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <DatePicker
                  label="Exame médico"
                  maxDate={new Date()}
                  control={control}
                  error={errors.healthCheck}
                  {...register('healthCheck')}
                />
              </GridItem>

              <GridItem colSpan={[12, 12, 6]}>
                <ReactSelect
                  label="Categoria"
                  name="categoryId"
                  options={categorySelectOptions}
                  control={control}
                  error={errors.categoryId}
                />
              </GridItem>
            </Grid>

            <MaskedInput
              label="Biografia"
              as="textarea"
              minHeight="160px"
              resize="none"
              py="2"
              error={errors.bio}
              {...register('bio')}
            />
          </VStack>

          <Divider my="6" borderColor="gray.300" />

          <AddressForm />

          <Flex mt="12" justify="flex-end">
            <ButtonGroup>
              <Button colorScheme="blackAlpha" onClick={goBack}>
                Cancelar
              </Button>

              <Button
                type="submit"
                colorScheme="green"
                isLoading={formState.isSubmitting}
              >
                Salvar
              </Button>
            </ButtonGroup>
          </Flex>
        </Box>
      </FormProvider>
    </DefaultLayout>
  );
};
